import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import MainLayout from '../../../layouts/MainLayout';
import { Button, Card, Descriptions, Tag, Avatar, Space, message } from 'antd';
import { EditOutlined, ArrowRightOutlined, UserOutlined } from '@ant-design/icons';
import { DetailPageSkeleton } from '../../../components/SkeletonLoaders';
import { translateRoleName } from '../../../utils/formHelpers';
import { formatPriceToman } from '../../../utils/formatPriceToman';
import { useDetailReturnRefs } from '../../../hooks/useListReturnState';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { employeesApi } from '../../../utils/api';
import { toJalali } from '../../../utils/dateUtils';

const languageMap = {
  farsi: 'فارسی',
  fa: 'فارسی',
  english: 'انگلیسی',
  en: 'انگلیسی',
  arabic: 'عربی',
  ar: 'عربی',
};

const translateLanguage = (lang) => {
  if (!lang) return '--';
  const lower = String(lang).toLowerCase();
  return languageMap[lower] || lang;
};

const employmentTypeFa = {
  full_time: 'تمام وقت',
  part_time: 'پاره وقت',
  internship: 'کارآموزی',
  contract: 'قراردادی',
};

const maritalFa = {
  single: 'مجرد',
  married: 'متاهل',
};

const genderFa = {
  male: 'مرد',
  female: 'زن',
};

const ViewEmployee = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { returnToRef, buildGoBackState } = useDetailReturnRefs();
  const { canEdit } = useResourcePermissions('employees');
  const [loading, setLoading] = useState(true);
  const [employee, setEmployee] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await employeesApi.getById(id);
        const item = res?.data || res?.employee || res || {};
        const detail = item.employee_detail || item.employeeDetail || {};
        if (cancelled) return;
        setEmployee({
          id: item.id,
          employeeId: detail.employee_id || item.employee_id,
          name: item.name,
          email: item.email,
          mobile: item.mobile,
          department: detail.department?.team_name || detail.department?.name || '--',
          jobTitle: detail.designation?.name || '--',
          position: detail.designation?.name || '--',
          joiningDate: detail.joining_date ? toJalali(detail.joining_date) : '--',
          dateOfBirth: detail.date_of_birth ? toJalali(detail.date_of_birth) : '--',
          nationalId: item.national_id || '--',
          fatherName: item.father_name || '--',
          insuranceCode: item.insurance_code || '--',
          gender: genderFa[item.gender] || item.gender || '--',
          role: (() => {
            const rolesList = Array.isArray(item.roles) ? item.roles : [];
            const activeRole = rolesList.find((r) => r.name !== 'employee') || rolesList.find((r) => r.name === 'employee') || rolesList[0];
            return activeRole?.display_name || activeRole?.name || item.role?.display_name || item.role?.name || '--';
          })(),
          reportsTo:
            (typeof detail.reporting_to === 'object'
              ? detail.reporting_to?.name
              : null) ||
            detail.reportingTo?.name ||
            '--',
          language: translateLanguage(item.locale),
          address: detail.address || '--',
          about: detail.about_me || '--',
          status: item.status || 'active',
          allowLogin: item.login === 'enable' || item.login === 'yes',
          emailNotifications:
            item.email_notifications === 1 || item.email_notifications === 'yes',
          skills: detail.skills || item.skills || '--',
          hourlyRate: detail.hourly_rate,
          employmentType:
            employmentTypeFa[detail.employment_type] || detail.employment_type || '--',
          maritalStatus: maritalFa[detail.marital_status] || detail.marital_status || '--',
          businessAddress: detail.company?.app_name || '--',
          imageUrl: item.image_url || item.image,
        });
      } catch (error) {
        console.error('خطا در دریافت کارمند:', error);
        message.error('خطا در دریافت اطلاعات کارمند');
        if (!cancelled) setEmployee(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return (
      <MainLayout>
        <div style={{ fontFamily: 'CustomFont' }}>
          <DetailPageSkeleton />
        </div>
      </MainLayout>
    );
  }

  if (!employee) {
    return (
      <MainLayout>
        <div className="p-6" style={{ fontFamily: 'CustomFont' }}>
          <Card>
            <p>اطلاعات کارمند یافت نشد.</p>
            <Button onClick={() => navigate(returnToRef.current || '/hr/employees')}>
              بازگشت
            </Button>
          </Card>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                <UserOutlined className="text-blue-600 dark:text-blue-400 text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">
                  مشاهده کارمند
                </h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm m-0">
                  {employee.employeeId || `#${employee.id}`}
                </p>
              </div>
            </div>
            <Space>
              <Button
                icon={<ArrowRightOutlined />}
                onClick={() =>
                  navigate(returnToRef.current || '/hr/employees', {
                    state: buildGoBackState(),
                  })
                }
                style={{ fontFamily: 'CustomFont' }}
              >
                بازگشت
              </Button>
              {canEdit ? (
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={() =>
                    navigate(`/hr/employees/${id}/edit`, {
                      state: { returnTo: returnToRef.current, ...buildGoBackState() },
                    })
                  }
                  className="bg-blue-600 hover:bg-blue-700 border-0"
                  style={{ fontFamily: 'CustomFont' }}
                >
                  ویرایش
                </Button>
              ) : null}
            </Space>
          </div>

          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            <Card className="shadow-sm border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 mb-6">
              <div className="flex items-center gap-4 mb-2">
                <Avatar
                  size={64}
                  src={employee.imageUrl}
                  style={{ backgroundColor: '#1890ff' }}
                  icon={<UserOutlined />}
                >
                  {employee.name ? employee.name.charAt(0) : ''}
                </Avatar>
                <div>
                  <h2
                    className="text-xl font-bold text-gray-800 dark:text-gray-200 m-0"
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    {employee.name}
                  </h2>
                  <p
                    className="text-gray-500 dark:text-gray-400 text-sm m-0"
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    {employee.position}{' '}
                    {employee.department && employee.department !== '--'
                      ? `- ${employee.department}`
                      : ''}
                  </p>
                  <Tag
                    color={employee.status === 'active' ? 'green' : 'red'}
                    className="mt-2"
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    {employee.status === 'active' ? 'فعال' : 'غیرفعال'}
                  </Tag>
                </div>
              </div>
            </Card>

            <Card
              title={<span style={{ fontFamily: 'CustomFont' }}>اطلاعات شخصی و شغلی</span>}
              className="shadow-sm border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 mb-6"
            >
              <Descriptions
                bordered
                column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }}
                style={{ fontFamily: 'CustomFont' }}
              >
                <Descriptions.Item label="نام کارمند">{employee.name}</Descriptions.Item>
                <Descriptions.Item label="ایمیل">{employee.email || '--'}</Descriptions.Item>
                <Descriptions.Item label="موبایل">{employee.mobile || '--'}</Descriptions.Item>
                <Descriptions.Item label="دپارتمان">{employee.department}</Descriptions.Item>
                <Descriptions.Item label="سمت شغلی">{employee.jobTitle}</Descriptions.Item>
                <Descriptions.Item label="تاریخ پیوستن">{employee.joiningDate}</Descriptions.Item>
                <Descriptions.Item label="تاریخ تولد">{employee.dateOfBirth}</Descriptions.Item>
                <Descriptions.Item label="کدملی">{employee.nationalId}</Descriptions.Item>
                <Descriptions.Item label="نام پدر">{employee.fatherName}</Descriptions.Item>
                <Descriptions.Item label="کد بیمه">{employee.insuranceCode}</Descriptions.Item>
                <Descriptions.Item label="جنسیت">{employee.gender}</Descriptions.Item>
                <Descriptions.Item label="نقش کاربر">
                  {translateRoleName(employee.role)}
                </Descriptions.Item>
                <Descriptions.Item label="گزارش‌دهنده به">{employee.reportsTo}</Descriptions.Item>
                <Descriptions.Item label="زبان">{employee.language}</Descriptions.Item>
                <Descriptions.Item label="نشانی" span={2}>
                  {employee.address}
                </Descriptions.Item>
                <Descriptions.Item label="درباره" span={2}>
                  {employee.about}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            <Card
              title={<span style={{ fontFamily: 'CustomFont' }}>سایر اطلاعات</span>}
              className="shadow-sm border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800"
            >
              <Descriptions
                bordered
                column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }}
                style={{ fontFamily: 'CustomFont' }}
              >
                <Descriptions.Item label="اجازه ورود؟">
                  <Tag
                    color={employee.allowLogin ? 'green' : 'red'}
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    {employee.allowLogin ? 'بله' : 'خیر'}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="دریافت اعلان‌های ایمیلی؟">
                  <Tag
                    color={employee.emailNotifications ? 'green' : 'red'}
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    {employee.emailNotifications ? 'بله' : 'خیر'}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="مهارت‌ها">{employee.skills}</Descriptions.Item>
                <Descriptions.Item label="نرخ ساعتی">
                  {employee.hourlyRate ? formatPriceToman(employee.hourlyRate) : '--'}
                </Descriptions.Item>
                <Descriptions.Item label="نوع استخدام">
                  {employee.employmentType}
                </Descriptions.Item>
                <Descriptions.Item label="وضعیت تأهل">
                  {employee.maritalStatus}
                </Descriptions.Item>
                <Descriptions.Item label="نشانی کسب و کار" span={2}>
                  {employee.businessAddress}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default ViewEmployee;
